import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import * as FactoryListActions from './factory-list.actions';
import { FactoryListFacade } from './factory-list.facade';
import { TFactoryListInfoActionType } from './factory-list.models';
import { switchMap, map, catchError, take, finalize } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class FactoryListEffects {
  constructor(
    private actions$: Actions,
    private plani8DataService: Plani8DataService,
    private factoryListFacade: FactoryListFacade,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private router: Router,
  ) {}

  getFactoryList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryListInfoActionType.GetFactoryList),
      switchMap(() =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl] as const;
          }),
        ),
      ),
      switchMap(([loaderEl]) =>
        this.plani8DataService.fetchFactoryListData().pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new FactoryListActions.GetFactoryListInfoSuccess(res)),
          catchError(() => {
            return of(new FactoryListActions.GetFactoryListInfoFailure());
          }),
        ),
      ),
    ),
  );

  allocateProductTypesToFactories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryListInfoActionType.AllocateProductTypesToFactories),
      switchMap((action: FactoryListActions.AllocateProductTypesToFactories) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.allocateProductTypesToFactories(action.productTypesAndFactoryIds).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.router.navigate(['/factories'], { queryParams: { clearProductTypesToAllocate: true } });
            this.appNotificationUtilClass.presentToast('Bulk allocation is complete.');
            return new FactoryListActions.AllocateProductTypesToFactoriesSuccess();
          }),
          catchError(() => {
            return of(new FactoryListActions.AllocateProductTypesToFactoriesFailure());
          }),
        ),
      ),
    ),
  );

  getFactoryUom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryListInfoActionType.GetFactoryUom),
      switchMap((action: FactoryListActions.GetFactoryUom) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.fetchFactoryUom(action.factoryId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new FactoryListActions.GetFactoryUomSuccess(res)),
          // catchError((err) => {
          //   console.log(err);
          // }),
        ),
      ),
    ),
  );

  updateFactoryUom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryListInfoActionType.UpdateFactoryUom),
      switchMap((action: FactoryListActions.UpdateFactoryUom) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.updateFactoryUom(action.factoryId, action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((msg) => new FactoryListActions.UpdateFactoryUomSuccess(msg)),
        ),
      ),
    ),
  );
}
